import React,{useState,useEffect} from "react";
import { useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import metadata from "../../metadata.json";
import apiClient from "../../api/apiClient";

const MainForm = ({
  register,
  handleSubmit,
  errors,
  loading,
  isValid,
  control,
  watch,
  dbVersion: dbVersionProp
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "domains",
  });

  const [dbVersion, setDbVersion] = useState(dbVersionProp || null);

  useEffect(() => {
    if (dbVersionProp) {
      setDbVersion(dbVersionProp);
      return;
    }
    apiClient.get("/versions/latest")
      .then((res) => {
        if (res.data?.version) setDbVersion(res.data.version);
      })
      .catch(() => {});
  }, [dbVersionProp]);

  const watchedDomains = watch("domains");

  const validateDuplicate = (value, index) => {
    if (!value || !watchedDomains) return true;
    const normalizedValue = value.toLowerCase().trim();
    const duplicateIndex = watchedDomains.findIndex(
      (domain, i) => i !== index && domain?.value?.toLowerCase().trim() === normalizedValue
    );
    return duplicateIndex === -1 || "This domain is already added";
  };

  const validateDomainFormat = (value) => {
    if (!value) return true;
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?))*\.[a-zA-Z]{2,}$/;
    return domainRegex.test(value.trim()) || "Please enter a valid domain (e.g., example.com)";
  };

  // Initialize with one domain if empty
  useEffect(() => {
    if (fields.length === 0) {
      append({ value: "" }); // ✅ append object, not string
    }
  }, [fields.length, append]);

  const [showMultipleDomains, setShowMultipleDomains] = useState(false);

  const handleAddDomain = () => {
    if (fields.length < 20) {
      append({ value: "" }); // ✅ correct way to append
    } else {
      toast.error("A maximum of 20 domains can be added.");
    }
  };

  const handleDisabledClick = () => {
    if (fields.length >= 20) {
      toast.error("A maximum of 20 domains can be added.");
    }
  };

  const handleRemoveDomain = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Update showMultipleDomains based on fields length
  useEffect(() => {
    setShowMultipleDomains(fields.length > 1);
  }, [fields.length]);

  return (
    <div
      className="mx-auto shadow p-4 rounded-4 bg-white"
      style={{ maxWidth: "1200px" }}
    >
      <div className="text-center">
        <h4 className="fw-bold">Security Assessment Request</h4>
        <p className="text-muted mb-0 mb-lg-3">Request a comprehensive cybersecurity assessment for your domain.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div
          className={showMultipleDomains ? "mb-2 scroll-container" : ""}
          style={{
            height: showMultipleDomains ? "375px" : "auto",
            overflowY: showMultipleDomains ? "scroll" : "auto",
            overflowX: "hidden",
          }}
        >
          {/* First/Last Name */}
          <div className="row">
            <div className="col-md-6 mb-1 mb-lg-3 mb-md-0">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                className="form-control py-2"
                {...register("firstName", {
                  required: "First name is required",
                  validate: (value) => value.trim() !== "" || "This field is required",
                })}
                placeholder="First Name"
                style={{ boxShadow: "none" }}
              />
              {errors.firstName && (
                <span className="error-text">{errors.firstName.message}</span>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                className="form-control py-2"
                {...register("lastName", { 
                  required: "Last name is required",
                  validate: (value) => value.trim() !== "" || "This field is required",
                })}
                placeholder="Last Name"
                style={{ boxShadow: "none" }}
              />
              {errors.lastName && (
                <span className="error-text">{errors.lastName.message}</span>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="mb-1 mb-lg-3 position-relative">
            <label className="form-label">
              Email Address *&nbsp;
              <span className="text-muted small">
                (Cyber Risk Report will be delivered to this email address)
              </span>
            </label>
            <input
              type="email"
              className="form-control py-2 pe-5"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              placeholder="Email Address"
              style={{ boxShadow: "none" }}
            />
            {errors.email && (
              <span className="error-text">{errors.email.message}</span>
            )}
          </div>

          {/* Domains Section */}
          <div>
            <div className="d-flex align-items-center justify-content-between">
              <label className="form-label mb-0">Domain to Assess *</label>
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-link p-0 ms-2"
                  title="Add More Domain"
                  onClick={fields.length >= 20 ? handleDisabledClick : handleAddDomain}
                  style={{ 
                    fontSize: "16px", 
                    color: fields.length >= 20 ? "#6c757d" : "#2563eb",
                    opacity: fields.length >= 20 ? 0.5 : 1,
                    cursor: fields.length >= 20 ? "not-allowed" : "pointer"
                  }}
                >
                  <i className="bi bi-plus-circle"></i>
                </button>

                {fields.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-2"
                    title="Remove Last Domain"
                    onClick={() => handleRemoveDomain(fields.length - 1)}
                    style={{ fontSize: "16px", color: "#dc2626" }}
                  >
                    <i className="bi bi-dash-circle"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Render Domains */}
            {showMultipleDomains ? (
              <div className="row">
                {fields.map((field, index) => (
                  <div key={field.id} className="col-6 mb-2">
                    <div className="input-group">
                      <span className="input-group-text">https://</span>
                      <input
                        type="text"
                        className="form-control py-2"
                        {...register(`domains.${index}.value`, {
                          required:
                            index === 0
                              ? "At least one domain is required"
                              : false,
                          validate: {
                            duplicate: (value) => validateDuplicate(value, index),
                            format: validateDomainFormat,
                          },
                        })}
                        placeholder="example.com"
                        style={{ boxShadow: "none" }}
                      />
                    </div>
                    {errors.domains?.[index]?.value && (
                      <span className="error-text">
                        {errors.domains[index].value.message}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              fields.map((field, index) => (
                <div key={field.id} className="mb-2">
                  <div className="input-group">
                    <span className="input-group-text">https://</span>
                    <input
                      type="text"
                      className="form-control py-2"
                      {...register(`domains.${index}.value`, {
                        required:
                          index === 0
                            ? "At least one domain is required"
                            : false,
                        validate: {
                          duplicate: (value) => validateDuplicate(value, index),
                          format: validateDomainFormat,
                        },
                      })}
                      placeholder="example.com"
                      style={{ boxShadow: "none" }}
                    />
                  </div>
                  {errors.domains?.[index]?.value && (
                    <span className="error-text">
                      {errors.domains[index].value.message}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          <small className="text-muted d-block mt-1 domain_name">
            Enter just the domain (e.g., example.com). Your email domain must match.
          </small>
        </div>

        {/* Terms Agreement */}
        <div className="mb-3">
          <div className="form-check d-flex align-items-center">
            <input
              className="form-check-input"
              type="checkbox"
              {...register("agree", { required: "❌ You must agree to the terms before continuing." })}
              id="tos"
              style={{ boxShadow: "none" }}
            />
            <label className="form-check-label ms-2 mt-1 terms-label" htmlFor="tos">
              I agree to understand that I may only assess domains I own or have explicit permission to scan.
            </label>
          </div>
          {errors.agree && (
            <span className="error-text">{errors.agree.message}</span>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="btn w-100 text-white px-3 py-2 rounded-4 fw-bold"
          style={{
            background: "linear-gradient(to right, #2563eb, #4338ca)",
            opacity: !isValid ? 0.7 : 1,
          }}
          disabled={loading || !isValid}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              Submitting...
            </>
          ) : (
            <>
              <i className="bi bi-shield-lock-fill me-2" />
              Start Security Assessment
            </>
          )}
        </button>
      </form>

      {/* Footer */}
      <div
        className="alert d-flex justify-content-end align-items-center rounded-2 mt-3 px-3 py-1"
        style={{
          backgroundColor: "rgb(239 246 255)",
          border: "1px solid rgb(191 219 254)",
        }}
      >
        <p className="mb-0 version">
          {dbVersion ? `v${dbVersion}` : `v${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}`}
        </p>
      </div>
    </div>
  );
};

export default MainForm;
